import { makeStyles } from "tss-react/mui";
import { MiChip, MiIcon } from "@miplatform/components";
import { emailRegex } from "@miplatform/constants";
import { getInitials, getUniqueArrayByField } from "@miplatform/utils";
import { mdiExclamation } from "@mdi/js";
import {
  Autocomplete,
  MenuItem,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { Box } from "@mui/system";

export const EmailUserAutocomplete = ({
  label,
  options,
  onChange,
  value,
  ...props
}) => {
  const { classes, theme } = useStyles();

  const autocompleteFilterOptions = createFilterOptions({
    ignoreCase: true,
    matchFrom: "start",
    limit: 40,
  });

  return (
    <Autocomplete
      multiple
      disableClearable
      freeSolo
      clearOnBlur
      limitTags={2}
      data-testId={props["data-testId"]}
      options={options}
      value={value}
      filterOptions={autocompleteFilterOptions}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id || option.email}
      onChange={(_e, newValue) => {
        const stringIndex = newValue.findIndex(
          (option) => typeof option === "string"
        );
        if (stringIndex !== -1) {
          if (
            newValue.some((option) => option.value === newValue[stringIndex])
          ) {
            newValue.splice(stringIndex, 1);
          } else {
            newValue[stringIndex] = {
              id: `new-${Date.now()}`,
              email: newValue[stringIndex],
              isError: !emailRegex.test(newValue[stringIndex]),
            };
          }
        }

        onChange(getUniqueArrayByField(newValue, "email"));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={label}
          onBlur={({ target }) => {
            if (
              !value.some(({ email }) => email === target.value) &&
              target.value
            ) {
              onChange([
                ...value,
                {
                  id: `new-${Date.now()}`,
                  email: target.value,
                  isError: !emailRegex.test(target.value),
                },
              ]);
            }
          }}
        />
      )}
      renderValue={(selected, getItemProps) =>
        selected.map((option, index) => (
          <MiChip
            key={option.id || option.email}
            label={`${option.name || option.email}${
              option.title ? ` | ${option.title}` : ""
            }`}
            className={classes.chip}
            style={
              option.isError
                ? { border: `1px solid ${theme.palette.error.main}` }
                : {}
            }
            avatar={
              (!option.id || option.isError) && (
                <Box
                  className={classes.avatar}
                  style={
                    option.isError
                      ? {
                          backgroundColor: "transparent",
                          border: `1px solid ${theme.palette.error.main}`,
                        }
                      : undefined
                  }
                >
                  {option.isError ? (
                    <MiIcon
                      path={mdiExclamation}
                      color={theme.palette.error.main}
                      size={0.7}
                    />
                  ) : (
                    <Typography fontSize={10} color="common.white">
                      {getInitials(option.name)}
                    </Typography>
                  )}
                </Box>
              )
            }
            onDelete={getItemProps({ index }).onDelete}
          />
        ))
      }
      renderOption={(props, option) => (
        <MenuItem {...props} key={option.id || option.email}>
          <Box display="flex" alignItems="center">
            {!option.id && (
              <Box className={classes.avatar} width={20} height={20} mr={1}>
                <Typography fontSize={10} color="common.white">
                  {getInitials(option.name)}
                </Typography>
              </Box>
            )}
            <Typography variant="body2">
              {option.name}
              {option.title ? ` | ${option.title}` : ""}
            </Typography>
          </Box>
        </MenuItem>
      )}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  chip: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey.mediumLight,
    margin: theme.spacing(0, 0.5, 0.5, 0),
    height: 34,
  },
  avatar: {
    borderRadius: "50%",
    backgroundColor: theme.palette.grey.medium,
    color: theme.palette.common.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));
